import { isString } from 'lodash';

import {
  addToMaxSeverity,
  capitalizeFirstLetter,
  getSeverite,
  isLeftSeverityWorst,
  lowerCaseFirstLetter,
  removeTrailingDot,
} from './utils';
import { IntlShape, MessageDescriptor } from 'react-intl';
import { PatternRepresentation, PatternsList, PatternsSeverityKey } from 'app/utils/reports/types';

type Tail<T> = T extends [infer A, ...infer R] ? R : never;

class BaseReportGenerator {
  protected intl: IntlShape;
  protected formatMessage: (
    s: string | MessageDescriptor,
    ...args: Tail<Parameters<IntlShape['formatMessage']>>
  ) => string;
  protected formatNumber: IntlShape['formatNumber'];

  constructor(intl: IntlShape) {
    this.intl = intl;
    this.formatMessage = (
      obj: string | MessageDescriptor,
      values?: Record<string, string>
    ): string => {
      if (typeof obj === 'string' || obj instanceof String) {
        return intl.formatMessage({ id: obj as string }, values) as string;
      }
      return intl.formatMessage(obj, values) as string;
    };
    this.formatNumber = intl.formatNumber;
  }

  /**
   * Translate a given pattern into the locale.
   * Pattern can be either a [string] representation the intl key or an already translated message
   * or an array of parameters to pass to the formatMessage function.
   * @param {*} pattern String or array of the form [identifier, intl_key, args]
   * @param {*} severity Severity level use to pick alternative translation.
   * @returns Input pattern translated into text form.
   */
  formatPattern = (pattern: PatternRepresentation, severity?: PatternsSeverityKey | string) => {
    const [patternName, ...args] = isString(pattern) ? [pattern] : pattern.slice(1);
    let possibleIntlKeys = [`report.patterns.${patternName}`, `${patternName}`];
    if (severity) {
      const severityString = `_${severity.replace(/Patterns?/i, '')}`;
      const patternNameWithSeverity = patternName + severityString;
      possibleIntlKeys = [
        `report.patterns.${patternNameWithSeverity}`,
        `${patternNameWithSeverity}`,
        ...possibleIntlKeys,
      ];
    }
    const matchingKey = possibleIntlKeys.find(
      (intlKey) => this.intl.messages[intlKey] !== undefined
    );
    if (matchingKey) return this.formatMessage(matchingKey, ...args);

    return patternName;
  };

  formatMessageIfPlural = (messageId: string, isPlural: boolean) => {
    const plural = isPlural ? '_plural' : '';
    if (this.intl.messages[messageId + plural]) {
      return this.formatMessage(messageId + plural);
    }
    return this.formatMessage(messageId);
  };

  getFeminin = (pattern: string) => {
    if (this.formatMessage(pattern)) {
      return '_feminin';
    }
    return '';
  };

  enumeratePatterns = (
    patternsList: PatternsList,
    finalUnion = 'report.and',
    severity: PatternsSeverityKey = undefined
  ) =>
    this.enumerate(patternsList, finalUnion, (patternKey) =>
      this.formatPattern(patternKey, severity)
    );

  enumerate = (
    intlKeys: PatternsList,
    finalUnion = 'report.and',
    intlKeyFormatFunction = this.formatPattern
  ) => {
    let translatedEnumeration = '';
    intlKeys.forEach((intlKey, i) => {
      const patternString = removeTrailingDot(lowerCaseFirstLetter(intlKeyFormatFunction(intlKey)));
      if (i === 0) {
        translatedEnumeration += patternString;
      } else if (i === intlKeys.length - 1) {
        translatedEnumeration += ` ${this.formatMessage(finalUnion)} ${patternString}`;
      } else {
        translatedEnumeration += `, ${patternString}`;
      }
    });
    return translatedEnumeration;
  };

  compose = (
    pattern1: string,
    pattern2: string,
    listOfLists: PatternsList[],
    composition: string
  ) => {
    const severite1 = getSeverite(pattern1, ...listOfLists);
    const severite2 = getSeverite(pattern2, ...listOfLists);
    let pattern;
    if (isLeftSeverityWorst(severite1, severite2)) {
      pattern = `${this.formatMessage(`report.patterns.${pattern1}`)}. ${capitalizeFirstLetter(
        this.formatMessage(`report.${composition}_${severite2}${this.getFeminin(pattern2)}`, {
          0: this.formatMessage(`report.patterns.${pattern2}`),
        })
      )}`;
    } else {
      pattern = `${this.formatMessage(`report.patterns.${pattern2}`)}. ${capitalizeFirstLetter(
        this.formatMessage(`report.${composition}_${severite1}${this.getFeminin(pattern1)}`, {
          0: this.formatMessage(`report.patterns.${pattern1}`),
        })
      )}`;
    }
    addToMaxSeverity(pattern, [pattern1, pattern2], listOfLists);
  };

  join = (pattern1: string, pattern2: string, listOfLists: PatternsList[]) => {
    this.compose(pattern1, pattern2, listOfLists, 'aussi');
  };

  associate = (
    pattern1: string,
    pattern2: string,
    resultingPattern: string,
    listOfLists: PatternsList[]
  ) => {
    const severite2 = getSeverite(pattern2, ...listOfLists);
    const pattern = `${this.formatMessage(`report.patterns.${pattern1}`)} ${this.formatMessage(
      `report.associe_${severite2}${this.getFeminin(pattern1)}`,
      { 0: this.formatMessage(`report.patterns.${resultingPattern}`) }
    )}`;
    addToMaxSeverity(pattern, [pattern1, pattern2], listOfLists);
  };
}

export default BaseReportGenerator;
